import React, { useEffect, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import { ReportGenUser } from "../../../shared/interfaces/User";
import { POSITIONS } from "../../../shared/constants/userConstants";
import { ConfirmDialog } from "../../../shared/ui";

export interface EditRankDialogProps {
  open: boolean;
  user: ReportGenUser | null;
  isSaving: boolean;
  onClose: () => void;
  /** Receives the selected POSITIONS value (the backend's jobTitle). */
  onSave: (jobTitle: string) => void;
}

const EditRankDialog: React.FC<EditRankDialogProps> = ({
  open,
  user,
  isSaving,
  onClose,
  onSave,
}) => {
  const [rank, setRank] = useState("");

  // Re-seed from the row each time the dialog opens for a user. The current
  // rank may be a free-form value from a CSV import — only preselect it when
  // the picker actually offers it.
  useEffect(() => {
    if (open) {
      const current = (user?.rank ?? "").toUpperCase();
      setRank(POSITIONS.includes(current) ? current : "");
    }
  }, [open, user]);

  // Compare against the RAW stored value: a case-variant rank (e.g.
  // "Sergeant" written by the self-profile PUT, which doesn't uppercase)
  // must count as a change, or Save is permanently disabled on the very row
  // an admin would open to normalize it.
  const isChanged = rank !== "" && rank !== (user?.rank ?? "");

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={() => {
        if (isChanged && !isSaving) {
          onSave(rank);
        }
      }}
      icon={<MilitaryTechOutlinedIcon />}
      iconVariant="tinted"
      title="Edit Rank"
      description={
        user
          ? `Update the rank for ${[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}. Current rank: ${user.rank || "None"}.`
          : undefined
      }
      confirmLabel="Save"
      loading={isSaving}
      confirmDisabled={!isChanged}
    >
      <TextField
        select
        fullWidth
        label="Rank"
        value={rank}
        onChange={(e) => setRank(e.target.value)}
        sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      >
        {POSITIONS.map((position) => (
          <MenuItem key={position} value={position}>
            {position}
          </MenuItem>
        ))}
      </TextField>
    </ConfirmDialog>
  );
};

export default EditRankDialog;
